"use client";

import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { 
  X, 
  MenuIcon,
  Languages, 
  Barcode, 
  UserCircle, 
  Coins, 
  CreditCard 
} from "lucide-react";
import { selectUserProfile, selectUserFilialeProfile, selectIsFilialeProfileLoaded } from "@store/selectors/user.selectors";
import LogoutButton from "@components/header/LogoutButton";
import { updateSettings } from "@store/features/userSlice";
import { logout } from "@store/features/authSlice";
import { useRouter } from "next/navigation";
import { openQRCodeViewer, toggleCouponsAndPointsViewer, toggleEditProfile, toggleRevenuesAndPointsViewer } from "@store/features/userActionsSlice";
import { ROUTES } from "@/constants/routes";
import { selectBrandName } from "@/lib/store/features/styleSchemaSlice";
import clsx from "clsx";

export default function Menu() {
  const [isOpen, setIsOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const router = useRouter();

  const profile = useSelector(selectUserProfile);
  const filialeProfile = useSelector(selectUserFilialeProfile);
  const isFilialeLoaded = useSelector(selectIsFilialeProfileLoaded);
  const menuRef = useRef<HTMLDivElement>(null);
  const brand = useSelector(selectBrandName);

  // Bestimme, ob wir eine Filiale-Begrüßung oder normale User-Begrüßung zeigen
  const hasFilialeData = filialeProfile?.title_filiale || filialeProfile?.c_o;
  const showFilialeGreeting = isFilialeLoaded && hasFilialeData;

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  // Handle click outside to close menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node) && isOpen) {
        setIsOpen(false);
      }
    };

    // Handle scroll to close menu
    const handleScroll = () => {
      if (isOpen) {
        setIsOpen(false);
      }
    };

    // Add event listeners when menu is open
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
    }

    // Clean up event listeners
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const handleLanguageChange = () => {
    if (i18n.language === "en") {
      i18n.changeLanguage("de");
      dispatch(updateSettings({ lang: "de" }));
    } else {
      i18n.changeLanguage("en");
      dispatch(updateSettings({ lang: "en" }));
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    router.push(ROUTES.root);
  };

  const handleQRCodeClick = () => {
    dispatch(openQRCodeViewer());
    setIsOpen(false);
  };

  const handleEditProfileClick = () => {
    dispatch(toggleEditProfile());
    setIsOpen(false);
    
    // Add small delay to allow the panel to render before scrolling
    setTimeout(() => {
      const panel = document.getElementById('edit-profile-panel');
      if (panel) {
        panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 300);
  };

  const handleRevenuesAndPointsClick = () => {
    dispatch(toggleRevenuesAndPointsViewer());
    setIsOpen(false);
    
    // Add small delay to allow the panel to render before scrolling
    setTimeout(() => {
      const panel = document.getElementById('revenues-points-panel');
      if (panel) {
        panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 300);
  };

  const handleCouponsAndPointsClick = () => {
    dispatch(toggleCouponsAndPointsViewer());
    setIsOpen(false);
    
    // Add small delay to allow the panel to render before scrolling
    setTimeout(() => {
      const panel = document.getElementById('coupons-points-panel');
      if (panel) {
        panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 300);
  };
  
  return (
    <div className="relative" ref={menuRef}>
      <motion.button
        onClick={toggleMenu}
        className={clsx("flex items-center justify-center p-2 text-xl transition-colors", {
          "text-white":  brand !== "cosmo" && brand !== "hairexpress" && brand !== "styleboxx"
        })}
        aria-label={isOpen ? t("menu.close") : t("menu.open")}
        title={isOpen ? t("menu.close") : t("menu.open")}
        whileTap={{ scale: 0.95 }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={isOpen ? 'close' : 'open'}
            initial={{ rotate: -90, opacity: 0 }}
            animate={{ rotate: 0, opacity: 1 }}
            exit={{ rotate: 90, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {isOpen ? <X size={24} /> : <MenuIcon size={24} />}
          </motion.div>
        </AnimatePresence>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div 
            className="absolute top-full right-0 mt-2 w-96 app-bg-secondary rounded-lg shadow-lg overflow-hidden z-50"
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
          >
            <motion.div 
              className="p-4 border-b border-gray-700 dark:border-gray-600"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
            >
              <div className="text-white font-exosoft">
                {showFilialeGreeting ? (
                  <div className="text-sm">
                    {t("filiale.dashboard.welcome", { filialeName: filialeProfile.c_o || filialeProfile.title_filiale })}
                  </div>
                ) : (
                  <>
                    <div className="text-sm font-bold opacity-80">{t("app.greating")},</div>
                    <div className="text-sm">{profile.salutation} {profile.firstname} {profile.lastname}</div>
                  </>
                )}
              </div>
            </motion.div>
            
            <div className="p-2">
              <motion.div 
                className="flex items-center justify-between p-2 app-hover rounded-md"
                initial={{ opacity: 0, x: -5 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                onClick={handleLanguageChange}
              >
                <div className="flex items-center justify-between w-full pr-2">
                  <span className="text-white">{t("language.title")}</span>
                  <Languages size={18} className="text-white" />
                </div>
              </motion.div>

              {/* Zeige QR Code und User-spezifische Optionen nur für normale Benutzer */}
              {!showFilialeGreeting && (
                <>
                  <motion.div 
                    className="flex items-center justify-between p-2 app-hover rounded-md"
                    initial={{ opacity: 0, x: -5 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                    onClick={handleQRCodeClick}
                  >
                    <div className="flex items-center justify-between w-full pr-2">
                      <span className="text-white">{t("navigation.my-card")}</span>
                      <Barcode size={18} className="text-white" />
                    </div>
                  </motion.div>

                  <motion.div 
                    className="flex items-center justify-between p-2 app-hover rounded-md"
                    initial={{ opacity: 0, x: -5 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                    onClick={handleEditProfileClick}
                  >
                    <div className="flex items-center justify-between w-full pr-2">
                      <span className="text-white">{t("customerAccount.myProfile")}</span>
                      <UserCircle size={18} className="text-white" />
                    </div>
                  </motion.div>

                  <motion.div 
                    className="flex items-center justify-between p-2 app-hover rounded-md"
                    initial={{ opacity: 0, x: -5 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                    onClick={handleRevenuesAndPointsClick}
                  >
                    <div className="flex items-center justify-between w-full pr-2">
                      <span className="text-white">{t("customerAccount.pointsButton")}</span>
                      <Coins size={18} className="text-white" />
                    </div>
                  </motion.div>

                  <motion.div 
                    className="flex items-center justify-between p-2 app-hover rounded-md"
                    initial={{ opacity: 0, x: -5 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                    onClick={handleCouponsAndPointsClick}
                  >
                    <div className="flex items-center justify-between w-full pr-2">
                      <span className="text-white">{t("customerAccount.couponsButton")}</span>
                      <CreditCard size={18} className="text-white" />
                    </div>
                  </motion.div>
                </>
              )}
              
              <motion.div 
                className="p-2 app-hover rounded-md"
                initial={{ opacity: 0, x: -5 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.25 }}
                onClick={handleLogout}
              >
                <div className="flex items-center justify-between">
                  <span className="text-white">{t("auth.logout")}</span>
                  <LogoutButton />
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}